import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Pencil, Trash2, Plus, X, ExternalLink, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDraft = {
  slug: "",
  title: "",
  category: "",
  author: "Calmbreeze Engineering",
  read_minutes: 5,
  cover_image: "",
  excerpt: "",
  content: "",
  published_at: "",
};

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 140);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const BlogManager = ({ onChange }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null); // null | {mode:'create'|'edit', originalSlug, draft, saving, err}

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, { withCredentials: true });
      setPosts(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => setEditor({ mode: "create", originalSlug: null, draft: { ...emptyDraft }, saving: false, err: "" });
  const openEdit = (post) => setEditor({ mode: "edit", originalSlug: post.slug, draft: { ...post }, saving: false, err: "" });
  const closeEditor = () => setEditor(null);

  const remove = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${slug}`, { withCredentials: true });
      await refresh();
      onChange?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async () => {
    if (!editor) return;
    setEditor((s) => ({ ...s, saving: true, err: "" }));
    const payload = { ...editor.draft, read_minutes: Number(editor.draft.read_minutes) || 5 };
    if (!payload.published_at) delete payload.published_at;
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/admin/blog/posts`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/blog/posts/${editor.originalSlug}`, payload, { withCredentials: true });
      }
      await refresh();
      onChange?.();
      setEditor(null);
    } catch (e) {
      setEditor((s) => ({ ...s, saving: false, err: e?.response?.data?.detail || e?.message || "Save failed" }));
    }
  };

  const setField = (field, value) => setEditor((s) => ({ ...s, draft: { ...s.draft, [field]: value } }));

  return (
    <div className="p-2" data-testid="blog-manager">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="text-[13px] text-muted-foreground">
          {loading ? "Loading…" : `${posts.length} post${posts.length === 1 ? "" : "s"}`}
        </div>
        <button
          onClick={openCreate}
          data-testid="blog-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-white text-[12px] font-semibold hover:bg-brand-hover transition"
        >
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      {error && <div className="p-4 font-mono text-[12px] text-accent">{error}</div>}

      {!loading && posts.length === 0 && !error && (
        <div className="p-12 text-center">
          <div className="font-display font-semibold text-[1.2rem] mb-2">No posts yet.</div>
          <div className="text-[13px] text-muted-foreground">Click “New post” to write your first article.</div>
        </div>
      )}

      {posts.length > 0 && (
        <table className="w-full text-[13px]" data-testid="blog-table">
          <thead>
            <tr className="text-left font-mono text-[10px] tracking-overline uppercase text-muted border-b border-black/8">
              <th className="py-3 pl-4 pr-3">Published</th>
              <th className="py-3 pr-3">Title</th>
              <th className="py-3 pr-3">Category</th>
              <th className="py-3 pr-3">Read</th>
              <th className="py-3 pr-3">Slug</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.slug} className="border-b border-black/5 hover:bg-paper-cool/60" data-testid={`blog-row-${p.slug}`}>
                <td className="py-3 pl-4 pr-3 whitespace-nowrap text-muted-foreground">{fmtDate(p.published_at)}</td>
                <td className="py-3 pr-3 font-semibold text-ink">{p.title}</td>
                <td className="py-3 pr-3 text-ink/80">{p.category}</td>
                <td className="py-3 pr-3 text-ink/80 whitespace-nowrap">{p.read_minutes} min</td>
                <td className="py-3 pr-3 font-mono text-[11px] text-muted-foreground">{p.slug}</td>
                <td className="py-3 pr-4">
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={`/insights/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View live"
                      data-testid={`blog-view-${p.slug}`}
                      className="p-1.5 rounded hover:bg-black/5 text-ink/60 hover:text-ink"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => openEdit(p)}
                      title="Edit"
                      data-testid={`blog-edit-${p.slug}`}
                      className="p-1.5 rounded hover:bg-black/5 text-ink/60 hover:text-ink"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => remove(p.slug, p.title)}
                      title="Delete"
                      data-testid={`blog-delete-${p.slug}`}
                      className="p-1.5 rounded hover:bg-red-500/10 text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editor && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
          data-testid="blog-editor"
        >
          <div className="bg-white rounded-md w-full max-w-3xl shadow-2xl relative">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/8">
              <div>
                <div className="font-mono text-[10px] tracking-overline uppercase text-accent">
                  [ {editor.mode === "create" ? "New post" : "Edit post"} ]
                </div>
                <div className="font-display font-bold text-[1.25rem] mt-1">
                  {editor.mode === "create" ? "Publish a new article" : `Edit “${editor.originalSlug}”`}
                </div>
              </div>
              <button onClick={closeEditor} aria-label="Close" data-testid="blog-editor-close" className="p-2 hover:bg-black/5 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Title</label>
                <input
                  type="text"
                  value={editor.draft.title}
                  onChange={(e) => {
                    const v = e.target.value;
                    setEditor((s) => ({
                      ...s,
                      draft: {
                        ...s.draft,
                        title: v,
                        // auto-generate slug from title only for new posts, and only while the user hasn't manually edited it
                        slug: editor.mode === "create" && (!s.draft.slug || s.draft.slug === slugify(s.draft.title)) ? slugify(v) : s.draft.slug,
                      },
                    }));
                  }}
                  data-testid="blog-input-title"
                  placeholder="e.g. Why F-Gas Regulations 2026 change everything for facilities managers"
                  className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">
                    Slug (URL)
                  </label>
                  <input
                    type="text"
                    value={editor.draft.slug}
                    onChange={(e) => setField("slug", slugify(e.target.value))}
                    data-testid="blog-input-slug"
                    placeholder="lowercase-with-dashes"
                    className="w-full px-3 py-2 border border-black/12 rounded font-mono text-[12px] focus:outline-none focus:border-brand"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Live URL: <span className="font-mono">/insights/{editor.draft.slug || "…"}</span>
                  </div>
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Category</label>
                  <input
                    type="text"
                    value={editor.draft.category}
                    onChange={(e) => setField("category", e.target.value)}
                    data-testid="blog-input-category"
                    placeholder="e.g. Fire Safety, Compliance, IAQ"
                    className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Author</label>
                  <input
                    type="text"
                    value={editor.draft.author}
                    onChange={(e) => setField("author", e.target.value)}
                    data-testid="blog-input-author"
                    className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Read time (min)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={editor.draft.read_minutes}
                    onChange={(e) => setField("read_minutes", e.target.value)}
                    data-testid="blog-input-readtime"
                    className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">
                    Published (blank = now)
                  </label>
                  <input
                    type="date"
                    value={editor.draft.published_at ? editor.draft.published_at.slice(0, 10) : ""}
                    onChange={(e) =>
                      setField("published_at", e.target.value ? `${e.target.value}T09:00:00+00:00` : "")
                    }
                    data-testid="blog-input-date"
                    className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand"
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">Cover image URL</label>
                <input
                  type="url"
                  value={editor.draft.cover_image}
                  onChange={(e) => setField("cover_image", e.target.value)}
                  data-testid="blog-input-cover"
                  placeholder="https://images.pexels.com/…"
                  className="w-full px-3 py-2 border border-black/12 rounded font-mono text-[12px] focus:outline-none focus:border-brand"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">
                  Excerpt (used on card + SEO description)
                </label>
                <textarea
                  rows={2}
                  value={editor.draft.excerpt}
                  onChange={(e) => setField("excerpt", e.target.value)}
                  data-testid="blog-input-excerpt"
                  placeholder="One or two sentences summarising the article."
                  className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand resize-y"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-overline uppercase text-muted mb-1">
                  Content (Markdown)
                </label>
                <textarea
                  rows={14}
                  value={editor.draft.content}
                  onChange={(e) => setField("content", e.target.value)}
                  data-testid="blog-input-content"
                  placeholder={"## Heading\n\nParagraph body. Use ## for sub-headings, - for bullets, **text** for bold."}
                  className="w-full px-3 py-2 border border-black/12 rounded font-mono text-[13px] focus:outline-none focus:border-brand resize-y leading-relaxed"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Supports Markdown headings (##), bullets (-), and **bold**. Line breaks become paragraphs.
                </div>
              </div>

              {editor.err && (
                <div className="text-[13px] text-red-600 font-mono border border-red-200 bg-red-50 rounded px-3 py-2" data-testid="blog-editor-error">
                  {editor.err}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-black/8 bg-paper-cool">
              <button onClick={closeEditor} className="px-4 py-2 rounded-full border border-black/12 hover:bg-white text-[12px] font-semibold">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={editor.saving}
                data-testid="blog-editor-save"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-accent text-white text-[12px] font-semibold hover:bg-accent-hover transition disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {editor.saving ? "Publishing…" : editor.mode === "create" ? "Publish post" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
